import { MouseEvent } from 'react';
import { Stack } from '@deltasierra/react-components-core';
import { CheckCircle, DotsHorizontal, Download01, SearchLg } from '@deltasierra/react-icons';
import { Translations } from '@deltasierra/translations-react';
import { AssetPermission } from '@deltasierra/frontend-graphql-core';
import { AssetType } from './constants';
import { AssetLibraryGalleryGridAssetButton } from './AssetLibraryGalleryGridAssetButton';

export type AssetLibraryGalleryGridAssetButtonsProps = {
    assetType: AssetType;
    isSelected: boolean;
    onClickAssetPreview: () => void;
    onClickDownload: () => void;
    onClickOpenActions: (event: MouseEvent<HTMLElement>) => void;
    onClickSelectItem: () => void;
    permissions: AssetPermission[];
    t: Translations<'AssetLibrary'>;
};

export const AssetLibraryGalleryGridAssetButtons = ({
    assetType,
    isSelected,
    onClickAssetPreview,
    onClickDownload,
    onClickOpenActions,
    onClickSelectItem,
    permissions,
    t,
}: AssetLibraryGalleryGridAssetButtonsProps): JSX.Element => {
    const canReadAssets = permissions.includes(AssetPermission.Read);
    const canUpdateAssets = permissions.includes(AssetPermission.Update);
    const canDeleteAssets = permissions.includes(AssetPermission.Delete);

    const handleActionsClick = (event: MouseEvent<HTMLElement>) => {
        onClickOpenActions(event);
        event.stopPropagation();
    };

    const handleSelectClick = (event: MouseEvent<HTMLElement>) => {
        onClickSelectItem();
        event.stopPropagation();
    };

    const handlePreviewClick = (event: MouseEvent<HTMLElement>) => {
        onClickAssetPreview();
        event.stopPropagation();
    };

    const handleDownloadClick = (event: MouseEvent<HTMLElement>) => {
        onClickDownload();
        event.stopPropagation();
    };

    return (
        <Stack
            className="asset-library-gallery-grid-asset-buttons"
            spacing={1}
            sx={{
                opacity: isSelected ? 1 : 0,
                position: 'absolute',
                right: '8px',
                top: '8px',
                transition: 'opacity 0.2s ease-in-out',
            }}
        >
            {(canReadAssets || canUpdateAssets || canDeleteAssets) && (
                <AssetLibraryGalleryGridAssetButton
                    ariaLabel={t('Asset actions')}
                    dataCy="asset-library-gallery-grid-asset-button-actions"
                    icon={DotsHorizontal}
                    onClick={handleActionsClick}
                />
            )}
            {(canReadAssets || canUpdateAssets || canDeleteAssets) && (
                <AssetLibraryGalleryGridAssetButton
                    ariaLabel={t('Asset select')}
                    dataCy="asset-library-gallery-grid-asset-button-select"
                    icon={CheckCircle}
                    variant={isSelected ? 'selected' : 'default'}
                    onClick={handleSelectClick}
                />
            )}

            {assetType === AssetType.Asset && canReadAssets && (
                <>
                    <AssetLibraryGalleryGridAssetButton
                        ariaLabel={t('Download asset')}
                        dataCy="asset-library-gallery-grid-asset-button-download"
                        icon={Download01}
                        onClick={handleDownloadClick}
                    />
                    <AssetLibraryGalleryGridAssetButton
                        ariaLabel={t('Asset preview')}
                        dataCy="asset-library-gallery-grid-asset-button-preview"
                        icon={SearchLg}
                        onClick={handlePreviewClick}
                    />
                </>
            )}
        </Stack>
    );
};

AssetLibraryGalleryGridAssetButtons.displayName = 'AssetLibraryGalleryGridAssetButtons';
